import { SetupGraphNodeValue, SetupGraphNodeValueTypes } from './SetupGraphNodeValue.js';

export enum SetupGraphType {
  Furniture,
  FLayout,
}

/**
 * SetupGraphNode is a node in the diagram. It contains the id, position and rotation of the node.
 * The id is a 1 byte hex value, the position and rotation are 16 bit floats.
 * The payload is 7 bytes long.
 * [id, posx, posy, rotation] 1 2 2 2
 * rotation is in radians and must be between 0 and 2PI
 * @example
 * ```ts
 * const node = new SetupGraphNode(1, SetupGraphType.Furniture);
 * const buffer = node.toBuffer();
 * const node2 = SetupGraphNode.fromBuffer(buffer);
 *
 * console.log(node2.toJSON());
 * ```
 *
 */
export class SetupGraphNode {
  static spec = '0.1';
  static schema = ['u8', 'u8', 'f16', 'f16', 'f16'];
  static byteLengths = SetupGraphNode.schema.map((type) =>
    SetupGraphNodeValue.getByteLength(type as SetupGraphNodeValueTypes)
  );
  static byteLength = SetupGraphNode.byteLengths.reduce((a, b) => a + b, 0);

  private _view: DataView;

  private vid: SetupGraphNodeValue<'u8'>;
  private vtype: SetupGraphNodeValue<'u8'>;
  private vx: SetupGraphNodeValue<'f16'>;
  private vy: SetupGraphNodeValue<'f16'>;
  private vr: SetupGraphNodeValue<'f16'>;

  constructor(buffer: Buffer);
  constructor(id: number, type: SetupGraphType);
  constructor(mixed: number | Buffer, type?: SetupGraphType) {
    if (Buffer.isBuffer(mixed)) {
      this._view = new DataView(mixed.buffer, mixed.byteOffset, mixed.byteLength);
    } else {
      const buffer = new ArrayBuffer(SetupGraphNode.byteLength);
      this._view = new DataView(buffer);
    }

    this.vid = new SetupGraphNodeValue(this._view, 'u8');
    this.vtype = new SetupGraphNodeValue(this._view, 'u8', 1);
    this.vx = new SetupGraphNodeValue(this._view, 'f16', 2);
    this.vy = new SetupGraphNodeValue(this._view, 'f16', 4);
    this.vr = new SetupGraphNodeValue(this._view, 'f16', 6);

    if (!Buffer.isBuffer(mixed)) {
      this.id = mixed;
      this.type = type ?? SetupGraphType.Furniture;
      this.position = [0, 0];
      this.rotation = 0;
    }
  }

  /**
   * Returns the spec of the node
   */
  get spec() {
    return SetupGraphNode.spec;
  }

  /**
   * Returns the id of the node
   */
  get id() {
    return this.vid.value;
  }

  /**
   * Sets the id of the node
   */
  set id(id: number) {
    this.vid.value = id;
  }

  /**
   * Returns the type of the node
   */
  get type() {
    return this.vtype.value as SetupGraphType;
  }

  /**
   * Sets the type of the node
   */
  set type(type: SetupGraphType) {
    this.vtype.value = type;
  }

  /**
   * Returns the rotation of the node in radians
   */
  get rotation() {
    return this.vr.value;
  }

  /**
   * Sets the rotation of the node in radians
   */
  set rotation(rotation: number) {
    this.vr.value = rotation;
  }

  /**
   * Returns the position of the node
   */
  get position(): [number, number] {
    return [this.vx.value, this.vy.value];
  }

  /**
   * Sets the position of the node
   */
  set position(position: [number, number]) {
    this.vx.value = position[0];
    this.vy.value = position[1];
  }
  /**
   * Returns the node as a JSON object
   */
  toJSON() {
    return {
      id: this.id,
      type: this.type,
      position: this.position,
      rotation: this.rotation,
    };
  }

  /**
   * Returns a copy of the underlying buffer
   */
  toBuffer() {
    return Buffer.from(this._view.buffer);
  }

  /**
   * Creates a new SetupGraphNode from a buffer
   */
  static fromBuffer(buffer: Buffer) {
    return new SetupGraphNode(buffer);
  }

  static fromJSON = (json: any) => {
    return new SetupGraphNode(json.id, json.type);
  };
}

export default SetupGraphNode;
